import React, { useState } from 'react';
import Input from '../ui/Input';
import Button from '../ui/Button';
import { AlertCircle, Mail, Lock } from 'lucide-react';

interface LoginFormProps {
  onSubmit: (email: string, password: string) => Promise<void>;
  isLoading?: boolean;
}

const LoginForm: React.FC<LoginFormProps> = ({ onSubmit, isLoading = false }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsSubmitted(true);

    if (!email || !password) {
      setError('Veuillez remplir tous les champs');
      return;
    }

    // Validation basique de l'email
    if (!/\S+@\S+\.\S+/.test(email)) {
      setError('Veuillez entrer une adresse email valide');
      return;
    }

    try {
      await onSubmit(email, password);
      // Si la connexion réussit, le formulaire se réinitialisera via le parent
    } catch (err: any) {
      // Afficher un message d'erreur spécifique selon le type d'erreur
      if (err.message.includes('incorrect')) {
        setError('Email ou mot de passe incorrect');
      } else if (err.message.includes('désactivé')) {
        setError('Ce compte a été désactivé. Contactez votre administrateur.');
      } else if (err.message.includes('trop de tentatives')) {
        setError('Trop de tentatives échouées. Veuillez réessayer dans quelques minutes.');
      } else {
        setError('Une erreur est survenue lors de la connexion');
      }
    } finally {
      setIsSubmitted(false);
    }
  };

  return (
    <form className="space-y-6" onSubmit={handleSubmit}>
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start">
          <AlertCircle className="h-5 w-5 text-red-500 mt-0.5 mr-3 flex-shrink-0" />
          <div>
            <p className="text-red-700 font-medium">{error}</p>
            <p className="text-red-600 text-sm mt-1">
              Vérifiez vos identifiants et réessayez
            </p>
          </div>
        </div>
      )}

      <div>
        <Input
          label="Adresse email"
          type="email"
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            if (error && isSubmitted) setError('');
          }}
          required
          disabled={isLoading}
          placeholder="exemple@institution.com"
          icon={<Mail className="h-5 w-5 text-gray-400" />}
          autoComplete="email"
          error={isSubmitted && !email ? 'Ce champ est requis' : undefined}
        />
      </div>

      <div>
        <Input
          label="Mot de passe"
          type="password"
          value={password}
          onChange={(e) => {
            setPassword(e.target.value);
            if (error && isSubmitted) setError('');
          }}
          required
          disabled={isLoading}
          placeholder="••••••••"
          icon={<Lock className="h-5 w-5 text-gray-400" />}
          autoComplete="current-password"
          error={isSubmitted && !password ? 'Ce champ est requis' : undefined}
        />
      </div>

      <div className="flex items-center justify-between">
        <div className="flex items-center">
          <input
            id="remember-me"
            name="remember-me"
            type="checkbox"
            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            disabled={isLoading}
          />
          <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-900">
            Se souvenir de moi
          </label>
        </div>

        <div className="text-sm">
          <a 
            href="/forgot-password" 
            className="font-medium text-blue-600 hover:text-blue-500 transition-colors"
          >
            Mot de passe oublié ?
          </a>
        </div>
      </div>

      <div>
        <Button
          type="submit"
          variant="primary"
          className="w-full py-3 text-base font-medium shadow-md hover:shadow-lg transition-shadow"
          isLoading={isLoading}
          disabled={isLoading}
        >
          Se connecter
        </Button>
      </div>

      <div className="text-center">
        <p className="text-sm text-gray-600">
          Nouveau sur la plateforme ?{' '}
          <a 
            href="/register" 
            className="font-semibold text-blue-600 hover:text-blue-500 transition-colors"
          >
            S'inscrire maintenant
          </a>
        </p>
      </div>
    </form>
  );
};

export default LoginForm;